/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback} from 'react';
import {makeStyles} from '@material-ui/core';
import NotificationsIcon from '@material-ui/icons/Notifications';
import NotificationsNoneIcon from '@material-ui/icons/NotificationsNone';
import {useSignals} from '@preact/signals-react/runtime';
import {translate} from '@ion/core/src/locale/locale';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import Render from '@ion/dls/src/render/Render';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {isTerminalJobStatus} from '../JobSearchEngineUtils';
import {liveJobsEnabled} from '../signals/liveUpdates';
import {useWatchedJob} from '../useWatchedJob';

export interface WatchJobBellProps {
  jobId: number | string;
  status: string;
};

export const WATCH_JOB_BELL_TEST_ID = 'watch-job-bell';

const useStyles = makeStyles(() => ({
  bell : {
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'center',
    flex           : '0 0 20px',
    width          : 20,
    height         : 20,
    cursor         : 'pointer'
  }
}));

/**
 * Whether a bell is offered for a job in this state.
 *
 * `shown ⟺ enabled && (isWatched || !isTerminal)`. Three consequences, all
 * deliberate:
 *
 * - **No bell at all while the feature flag is off**, so the whole watch
 *   affordance is dark rather than present-but-inert. `enabled` is a parameter
 *   rather than a signal read so this stays a pure rule with one definition -
 *   the point of the helper is that no surface can drift from it.
 * - **A finished job cannot be watched.** No bell is offered, which is what
 *   makes `notifiedJobIds` prunable to `jobIds` on hydrate. It is a correctness
 *   constraint rather than a UI preference.
 * - **Unwatching a finished failed job is a one-way door** - the bell
 *   disappears, the urgent icon returns, and the bell cannot come back.
 */
export const isWatchBellVisible = (
  enabled: boolean,
  watched: boolean,
  status: string
): boolean => (
  enabled && (watched || !isTerminalJobStatus(status))
);

/**
 * The visibility rule, for the surfaces which have to render something *else*
 * in the same slot when the bell is not offered.
 *
 * @param   jobId The job the caller is rendering.
 * @param   status The job's `progressStatus`.
 * @returns `true` when a bell is offered for this job.
 */
export const useWatchBellVisible = (jobId: number | string, status: string): boolean => {
  useSignals();
  const {watched} = useWatchedJob(Number(jobId));

  return isWatchBellVisible(liveJobsEnabled.value, watched, status);
};

/**
 * The watch toggle: filled means the user will be notified when the job
 * finishes, outline means they will not. It owns the visibility rule, so every
 * surface which renders it inherits the same behaviour and cannot drift.
 *
 * ```typescript
 *
 *   import WatchJobBell from './cells/WatchJobBell';
 *
 *   <WatchJobBell jobId={data?.value} status={data?.data?.progressStatus} />
 *
 * ```
 */
export const WatchJobBell = ({jobId, status}: WatchJobBellProps) => {
  useSignals();
  const classes           = useStyles();
  const {watched, toggle} = useWatchedJob(Number(jobId));
  const label             = translate(watched
    ? 'jobsearchengine:notifications.unwatch'
    : 'jobsearchengine:notifications.watch');
  const Bell              = watched ? NotificationsIcon : NotificationsNoneIcon;

  const onToggle = useCallback((event: React.MouseEvent): void => {
    event.stopPropagation();
    toggle();
  }, [toggle]);

  return (
    <Render.If condition={isWatchBellVisible(liveJobsEnabled.value, watched, status)}>
      <Tooltip title={label} placement='right' arrow>
        <span className={classes.bell}
          data-testid={WATCH_JOB_BELL_TEST_ID}
          role='button'
          aria-label={label}
          onClick={onToggle}>
          <Bell fontSize='small'
            htmlColor={watched ? dlsColor('darkBlue') : dlsColor('grey3')} />
        </span>
      </Tooltip>
    </Render.If>
  );
};

export default React.memo(WatchJobBell);
